import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { User, Learnspace, Invitation, Course, isHubAdmin, isInOtherlab, isInImlab, Hub, hasProgramHub, isHubManager } from '@shared/models';
import { Router } from '@angular/router';
export enum IntercomTags {
  WONDA_PRO_SIGNUP_COMPLETE = 'WondaPro-user',
  WONDA_PRO_COMPLETED_QUALIFICATION = 'WondaPro-qual',
  WONDA_PRO_HAS_CREATED_PROGRAM = 'WondaPro-program1',
  WONDA_PRO_CAME_BACK = 'WondaPro-back1',
  WONDA_PRO_HAS_EDITED_SIMULATION = 'WondaPro-edit1',
  WONDA_PRO_HAS_CONSULTED_HELP = 'WondaPro-help',
  WONDA_PRO_PRICING_PAGE = 'WondaPro-pricing1',
  WONDA_PRO_CHECKOUT = 'WondaPro-checkout',
  WONDA_PRO_CHECKOUT_OK = 'WondaPro-checkoutOk'
}
@Injectable({ providedIn: 'root' })
export class InternalAnalyticsService {
  private intercomUser: any = null;
  private userId: string;
  constructor(private _router: Router, private _http: HttpClient) {}

  /**
   * Intercom is an analytics and communication tool to track user activity
   */
  private isIntercomDisabled(): boolean {
    return (<any>window).Intercom === undefined;
  }

  private shouldHideIntercomChat(): boolean {
    return /^\/player-view\//i.test(this._router.url);
  }

  public async initIntercomWithUser(user: User): Promise<void> {
    if (this.isIntercomDisabled()) {
      return;
    }
    this.userId = user.id;
    const intercomParams: any = {
      app_id: environment.intercom_app_id,
      name: user.first_name,
      email: user.email,
      user_hash: user.intercom_hash,
      admin: isHubAdmin(user),
      manager: isHubManager(user),
      other_hub: isInOtherlab(user),
      imlab_hub: isInImlab(user),
      hide_default_launcher: this.shouldHideIntercomChat()
    };
    (<any>window).Intercom('boot', intercomParams);
    this.intercomUser = await this.getIntercomUser(user.id);
    if (this.intercomUser && hasProgramHub(user)) {
      this.addIntercomUserTag(IntercomTags.WONDA_PRO_SIGNUP_COMPLETE);
      const lastSeenDate = new Date(this.intercomUser.last_seen_at * 1000).getTime();
      const now = new Date().getTime();
      const lastConnectionDiffHours = (now - lastSeenDate) / (1000 * 60 * 60);
      if (lastConnectionDiffHours > 24) {
        this.addIntercomUserTag(IntercomTags.WONDA_PRO_CAME_BACK);
      }
    }
  }

  public identifyHeapUser(user: User): void {
    if ((<any>window).heap === undefined) return;
    (<any>window).heap.identify(user.id);
    let mainHub = user.Hubs[0];
    if (user.preferences.defaultHub) {
      mainHub = user.Hubs.find((hub) => hub.id === user.preferences.defaultHub);
    }
    const customProperties = {
      email: user.email,
      admin: isHubAdmin(user),
      hubCount: user.Hubs.length,
      hubName: mainHub.urlTitle,
      plan: mainHub.plan,
      hubType: mainHub.hub_type
    };
    (<any>window).heap.addUserProperties(customProperties);
  }
  public hideChat(): void {
    if (this.isIntercomDisabled()) {
      return;
    }
    (<any>window).Intercom('update', {
      hide_default_launcher: true
    });
  }

  public showChat(): void {
    if (this.isIntercomDisabled()) {
      return;
    }
    (<any>window).Intercom('update', {
      hide_default_launcher: false
    });
  }

  public showNewMessage(): void {
    if (this.isIntercomDisabled()) {
      return;
    }
    (<any>window).Intercom('showNewMessage');
  }
  public disconnectIntercom(): void {
    if (this.isIntercomDisabled()) {
      return;
    }
    this.intercomUser = null;
    (<any>window).Intercom('shutdown');
  }

  public trackCourseCreation(course: Course, templateId: string, templateTitle: string): void {
    if (this.isIntercomDisabled()) {
      return;
    }
    (<any>window).Intercom('trackEvent', 'create-course', {
      id: course.id,
      title: course.title,
      template_id: templateId,
      template_title: templateTitle
    });
  }

  public trackCourseEdition(course: Course): void {
    if (this.isIntercomDisabled()) {
      return;
    }
    (<any>window).Intercom('trackEvent', 'edit-course', {
      id: course.id,
      title: course.title
    });
  }
  public trackLSCreation(ls: Learnspace): void {
    if (this.isIntercomDisabled()) {
      return;
    }
    (<any>window).Intercom('trackEvent', 'create-learnspace', {
      id: ls.id,
      title: ls.title
    });
  }

  public trackInvitation(invitation: Invitation): void {
    if (this.isIntercomDisabled()) {
      return;
    }
    (<any>window).Intercom('trackEvent', 'invite-to-learnspace', {
      id: invitation.id,
      recipient: invitation.recipient_mail,
      learnspace_id: invitation.target_id
    });
  }

  public trackPreviewEvent(event_data: any): void {
    if (this.isIntercomDisabled()) {
      return;
    }
    (<any>window).Intercom('trackEvent', 'preview-event', event_data);
  }
  public trackBillingPage(hub: Hub): void {
    if (this.isIntercomDisabled()) {
      return;
    }
    (<any>window).Intercom('trackEvent', 'billing-page-event', {
      id: hub.id,
      title: hub.title
    });
  }
  public trackBillingCheckout(hub: Hub, plan_code: string): void {
    if (this.isIntercomDisabled()) {
      return;
    }
    (<any>window).Intercom('trackEvent', 'checkout-page-event', {
      id: hub.id,
      title: hub.title,
      plan: plan_code
    });
  }
  public trackBillingReturn(hub: Hub): void {
    if (this.isIntercomDisabled()) {
      return;
    }
    (<any>window).Intercom('trackEvent', 'checkoutOK-page-event', {
      id: hub.id,
      title: hub.title
    });
  }
  public async getIntercomUser(id: string): Promise<any> {
    if (this.intercomUser) {
      return this.intercomUser;
    }
    return this._http.post(environment.coursesApiUrl + '/api/intercom/finduser', { user_id: id }).toPromise();
  }
  public async addIntercomUserTag(tag: string): Promise<void> {
    if (this.isIntercomDisabled()) {
      return;
    }
    if (!this.intercomUser) {
      this.intercomUser = await this.getIntercomUser(this.userId);
    }
    if (this.intercomUser.tags.length > 0 && this.intercomUserHasTag(tag)) return;
    await this._http.post(environment.coursesApiUrl + '/api/intercom/users/tags', { user_id: this.userId, tag: tag }).toPromise();
  }
  private intercomUserHasTag(tag: string) {
    return this.intercomUser.tags.find((t: any) => t.name == tag);
  }
}
